import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { apiService } from '../services/apiService';

const CompanyContext = createContext();

const initialState = {
  company: null,
  users: [],
  stockItems: [],
  warehouses: [],
  shelves: [],
  customers: [],
  suppliers: [],
  invoices: [],
  orders: [],
  workOrders: [],
  payments: [],
  collections: [],
  productionOrders: [],
  campaigns: [],
  coupons: [],
  employees: [],
  systemSettings: {},
  loading: false,
  error: null
};

const companyReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    
    case 'SET_COMPANY':
      return { ...state, company: action.payload };
    
    case 'SET_USERS':
      return { ...state, users: action.payload };
    
    case 'ADD_USER':
      return { ...state, users: [...state.users, action.payload] };
    
    case 'UPDATE_USER':
      return {
        ...state,
        users: state.users.map(user =>
          user.id === action.payload.id ? action.payload : user
        )
      };
    
    case 'DELETE_USER':
      return {
        ...state,
        users: state.users.filter(user => user.id !== action.payload)
      };
    
    case 'SET_STOCK_ITEMS':
      return { ...state, stockItems: action.payload };
    
    case 'ADD_STOCK_ITEM':
      return { ...state, stockItems: [...state.stockItems, action.payload] };
    
    case 'UPDATE_STOCK_ITEM':
      return {
        ...state,
        stockItems: state.stockItems.map(item =>
          item.id === action.payload.id ? action.payload : item
        )
      };
    
    case 'DELETE_STOCK_ITEM':
      return {
        ...state,
        stockItems: state.stockItems.filter(item => item.id !== action.payload)
      };
    
    case 'SET_WAREHOUSES':
      return { ...state, warehouses: action.payload };
    
    case 'SET_SHELVES':
      return { ...state, shelves: action.payload };
    
    case 'SET_CUSTOMERS':
      return { ...state, customers: action.payload };
    
    case 'SET_SUPPLIERS':
      return { ...state, suppliers: action.payload };
    
    case 'SET_INVOICES':
      return { ...state, invoices: action.payload };
    
    case 'SET_ORDERS':
      return { ...state, orders: action.payload };
    
    case 'SET_WORK_ORDERS':
      return { ...state, workOrders: action.payload };
    
    case 'SET_PAYMENTS':
      return { ...state, payments: action.payload };
    
    case 'SET_COLLECTIONS':
      return { ...state, collections: action.payload };
    
    case 'SET_PRODUCTION_ORDERS':
      return { ...state, productionOrders: action.payload };
    
    case 'SET_CAMPAIGNS':
      return { ...state, campaigns: action.payload };
    
    case 'SET_COUPONS':
      return { ...state, coupons: action.payload };
    
    case 'SET_EMPLOYEES':
      return { ...state, employees: action.payload };
    
    case 'SET_SYSTEM_SETTINGS':
      return { ...state, systemSettings: action.payload };
    
    default:
      return state;
  }
};

export const CompanyProvider = ({ children }) => {
  const [state, dispatch] = useReducer(companyReducer, initialState);

  // Load company information
  const loadCompany = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/company/info');
      dispatch({ type: 'SET_COMPANY', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load users
  const loadUsers = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/users');
      dispatch({ type: 'SET_USERS', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Create user
  const createUser = async (userData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.post('/users', userData);
      dispatch({ type: 'ADD_USER', payload: response.data });
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  // Update user
  const updateUser = async (userId, userData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.put(`/users/${userId}`, userData);
      dispatch({ type: 'UPDATE_USER', payload: response.data });
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await apiService.delete(`/users/${userId}`);
      dispatch({ type: 'DELETE_USER', payload: userId });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  // Load stock items
  const loadStockItems = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/stock/items');
      dispatch({ type: 'SET_STOCK_ITEMS', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Create stock item
  const createStockItem = async (itemData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.post('/stock/items', itemData);
      dispatch({ type: 'ADD_STOCK_ITEM', payload: response.data });
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  // Update stock item
  const updateStockItem = async (itemId, itemData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.put(`/stock/items/${itemId}`, itemData);
      dispatch({ type: 'UPDATE_STOCK_ITEM', payload: response.data });
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  // Delete stock item
  const deleteStockItem = async (itemId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await apiService.delete(`/stock/items/${itemId}`);
      dispatch({ type: 'DELETE_STOCK_ITEM', payload: itemId });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  // Load warehouses
  const loadWarehouses = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/warehouses');
      dispatch({ type: 'SET_WAREHOUSES', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load shelves
  const loadShelves = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/shelves');
      dispatch({ type: 'SET_SHELVES', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load customers
  const loadCustomers = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/customers');
      dispatch({ type: 'SET_CUSTOMERS', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load suppliers
  const loadSuppliers = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/suppliers');
      dispatch({ type: 'SET_SUPPLIERS', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load invoices
  const loadInvoices = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/invoices');
      dispatch({ type: 'SET_INVOICES', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load orders
  const loadOrders = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/orders');
      dispatch({ type: 'SET_ORDERS', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load work orders
  const loadWorkOrders = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/work-orders');
      dispatch({ type: 'SET_WORK_ORDERS', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load payments
  const loadPayments = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/payments');
      dispatch({ type: 'SET_PAYMENTS', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load collections
  const loadCollections = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/collections');
      dispatch({ type: 'SET_COLLECTIONS', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load production orders
  const loadProductionOrders = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/production/orders');
      dispatch({ type: 'SET_PRODUCTION_ORDERS', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load campaigns
  const loadCampaigns = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/campaigns');
      dispatch({ type: 'SET_CAMPAIGNS', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load coupons
  const loadCoupons = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/coupons');
      dispatch({ type: 'SET_COUPONS', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load employees
  const loadEmployees = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/employees');
      dispatch({ type: 'SET_EMPLOYEES', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load system settings
  const loadSystemSettings = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.get('/company/settings');
      dispatch({ type: 'SET_SYSTEM_SETTINGS', payload: response.data });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Update system settings
  const updateSystemSettings = async (settings) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.put('/company/settings', settings);
      dispatch({ type: 'SET_SYSTEM_SETTINGS', payload: response.data });
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  // Initialize company data
  useEffect(() => {
    loadCompany();
    loadUsers();
    loadStockItems();
    loadWarehouses();
    loadShelves();
    loadCustomers();
    loadSuppliers();
    loadInvoices();
    loadOrders();
    loadWorkOrders();
    loadPayments();
    loadCollections();
    loadProductionOrders();
    loadCampaigns();
    loadCoupons();
    loadEmployees();
    loadSystemSettings();
  }, []);

  const value = {
    ...state,
    loadCompany,
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
    loadStockItems,
    createStockItem,
    updateStockItem,
    deleteStockItem,
    loadWarehouses,
    loadShelves,
    loadCustomers,
    loadSuppliers,
    loadInvoices,
    loadOrders,
    loadWorkOrders,
    loadPayments,
    loadCollections,
    loadProductionOrders,
    loadCampaigns,
    loadCoupons,
    loadEmployees,
    loadSystemSettings,
    updateSystemSettings
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};
